/* Super Admin Panel — platform-level. role=super_admin için. */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Shield, Users, Activity, Search, Pause, Play, Trash2,
    User as UserIcon, Loader2, RefreshCw, Eye, AlertTriangle,
    CheckCircle2,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function SuperAdmin() {
    const navigate = useNavigate();
    const [tab, setTab] = useState('owners');
    const [stats, setStats] = useState(null);
    const [owners, setOwners] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [confirmText, setConfirmText] = useState('');

    const loadStats = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/superadmin/stats`, { headers: hdrs() });
            setStats(r.data);
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error('Super admin yetkin yok');
                navigate('/dashboard');
            }
        }
    }, [navigate]);

    const loadOwners = useCallback(async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/api/superadmin/owners`, {
                headers: hdrs(), params: { search, status: statusFilter, limit: 200 },
            });
            setOwners(r.data?.items || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Yüklenemedi');
        } finally { setLoading(false); }
    }, [search, statusFilter]);

    const loadLogs = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/api/superadmin/activity-log`, { headers: hdrs() });
            setLogs(r.data?.items || []);
        } catch { /* */ }
    }, []);

    useEffect(() => { loadStats(); }, [loadStats]);
    useEffect(() => {
        if (tab === 'owners') loadOwners();
        if (tab === 'logs') loadLogs();
    }, [tab, loadOwners, loadLogs]);

    const suspend = async (id) => {
        const reason = window.prompt('Askıya alma sebebi (opsiyonel):');
        if (reason === null) return;
        try {
            await axios.post(`${API}/api/superadmin/owners/${id}/suspend`, { reason }, { headers: hdrs() });
            toast.success('Askıya alındı'); loadOwners(); loadStats();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };
    const activate = async (id) => {
        try {
            await axios.post(`${API}/api/superadmin/owners/${id}/activate`, {}, { headers: hdrs() });
            toast.success('Aktifleştirildi'); loadOwners(); loadStats();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };
    const impersonate = async (o) => {
        if (!window.confirm(`${o.email} olarak giriş yapılacak. Devam?`)) return;
        try {
            const r = await axios.post(`${API}/api/superadmin/owners/${o.id}/impersonate`, {}, { headers: hdrs() });
            localStorage.setItem('admin_token_backup', localStorage.getItem('token'));
            localStorage.setItem('token', r.data.token);
            localStorage.setItem('user', JSON.stringify(r.data.user));
            localStorage.setItem('company', JSON.stringify(r.data.company));
            localStorage.setItem('impersonating', '1');
            toast.success(`${o.email} olarak giriş`);
            navigate('/dashboard'); window.location.reload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };
    const doDelete = async () => {
        if (confirmText !== 'DELETE') { toast.error('DELETE yaz'); return; }
        try {
            const r = await axios.delete(`${API}/api/superadmin/owners/${confirmDelete.id}?confirm=DELETE`, { headers: hdrs() });
            const total = Object.values(r.data?.deleted || {}).reduce((s, n) => s + n, 0);
            toast.success(`Silindi: ${confirmDelete.email} (${total} kayıt)`);
            setConfirmDelete(null); setConfirmText(''); loadOwners(); loadStats();
        } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
    };

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50">
                <div className="max-w-[1500px] mx-auto px-3 md:px-6 py-3 md:py-5">
                    <div className="flex items-center gap-3 mb-4">
                        <Shield size={22} className="text-rose-700" />
                        <h1 className="text-lg md:text-xl font-bold text-gray-900">Super Admin Panel</h1>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 bg-rose-100 text-rose-700 rounded-full">platform</span>
                    </div>

                    {stats && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <StatCard label="Toplam Owner" value={stats.total_owners} icon={Users} color="blue" />
                            <StatCard label="Aktif" value={stats.active} icon={CheckCircle2} color="emerald" />
                            <StatCard label="Askıda" value={stats.suspended} icon={Pause} color="amber" />
                            <StatCard label="Google ile Kayıt" value={stats.google_users} icon={UserIcon} color="purple" />
                        </div>
                    )}

                    {stats?.integrations && (
                        <div className="bg-white border border-gray-200 rounded-lg p-3 mb-4 flex items-center gap-3 flex-wrap text-xs">
                            <span className="text-gray-500 font-bold uppercase tracking-wider">Entegrasyonlar:</span>
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">Bol: {stats.integrations.bol}</span>
                            <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded">Telegram: {stats.integrations.telegram}</span>
                            <span className="px-2 py-1 bg-cyan-50 text-cyan-700 rounded">ShipsGo: {stats.integrations.shipsgo}</span>
                        </div>
                    )}

                    <div className="bg-white border border-gray-200 rounded-lg mb-3">
                        <div className="flex gap-1 p-1">
                            {[
                                { k: 'owners', l: 'Owners', icon: Users },
                                { k: 'logs', l: 'Aktivite Logu', icon: Activity },
                            ].map((t) => {
                                const Icon = t.icon;
                                const active = tab === t.k;
                                return (
                                    <button key={t.k} onClick={() => setTab(t.k)}
                                        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md ${
                                            active ? 'bg-rose-700 text-white' : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                        data-testid={`sa-tab-${t.k}`}>
                                        <Icon size={14} />{t.l}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-5">
                        {tab === 'owners' && (
                            <>
                                <div className="flex items-center gap-2 mb-3 flex-wrap">
                                    <div className="relative flex-1 min-w-[200px]">
                                        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input value={search} onChange={(e) => setSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && loadOwners()}
                                            placeholder="Email veya isim ara..."
                                            className="w-full h-9 pl-8 pr-3 text-sm border border-gray-300 rounded-md"
                                            data-testid="sa-search" />
                                    </div>
                                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                                        className="h-9 px-2 text-sm border border-gray-300 rounded-md"
                                        data-testid="sa-status-filter">
                                        <option value="all">Tümü</option>
                                        <option value="active">Aktif</option>
                                        <option value="suspended">Askıda</option>
                                    </select>
                                    <button onClick={loadOwners} disabled={loading}
                                        className="h-9 px-3 text-sm font-semibold text-white bg-rose-700 hover:bg-rose-800 disabled:opacity-50 rounded-md flex items-center gap-1.5">
                                        {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                                        Yenile
                                    </button>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead className="bg-gray-50 text-gray-600 uppercase">
                                            <tr>
                                                <th className="text-left p-2">Email</th>
                                                <th className="text-left p-2">İsim</th>
                                                <th className="text-left p-2">Şirket</th>
                                                <th className="text-left p-2">Rol</th>
                                                <th className="text-left p-2">Auth</th>
                                                <th className="text-left p-2">Durum</th>
                                                <th className="text-left p-2">Kayıt</th>
                                                <th className="text-right p-2">İşlemler</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {owners.map((o) => {
                                                const isAdmin = o.role === 'super_admin';
                                                return (
                                                    <tr key={o.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`sa-row-${o.id}`}>
                                                        <td className="p-2 font-mono">{o.email}</td>
                                                        <td className="p-2">{o.full_name || '—'}</td>
                                                        <td className="p-2 text-gray-600">{o.company_name}</td>
                                                        <td className="p-2">
                                                            {isAdmin ? (
                                                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-rose-100 text-rose-700 rounded">SUPER ADMIN</span>
                                                            ) : <span className="text-gray-500">Owner</span>}
                                                        </td>
                                                        <td className="p-2 text-gray-600">{o.auth_method === 'google' ? '🔑 Google' : 'Şifre'}</td>
                                                        <td className="p-2">
                                                            {o.is_suspended ? (
                                                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded">⏸ Askıda</span>
                                                            ) : (
                                                                <span className="px-1.5 py-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-700 rounded">✓ Aktif</span>
                                                            )}
                                                        </td>
                                                        <td className="p-2 text-gray-500">{(o.created_at || '').slice(0, 10)}</td>
                                                        <td className="p-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => impersonate(o)} disabled={isAdmin}
                                                                    className="h-7 w-7 flex items-center justify-center text-gray-500 hover:bg-blue-50 hover:text-blue-700 disabled:opacity-30 rounded"
                                                                    title="Impersonate" data-testid={`sa-impersonate-${o.id}`}>
                                                                    <Eye size={12} />
                                                                </button>
                                                                {o.is_suspended ? (
                                                                    <button onClick={() => activate(o.id)}
                                                                        className="h-7 w-7 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 rounded"
                                                                        title="Aktifleştir" data-testid={`sa-activate-${o.id}`}>
                                                                        <Play size={12} />
                                                                    </button>
                                                                ) : (
                                                                    <button onClick={() => suspend(o.id)} disabled={isAdmin}
                                                                        className="h-7 w-7 flex items-center justify-center text-amber-600 hover:bg-amber-50 disabled:opacity-30 rounded"
                                                                        title="Askıya al" data-testid={`sa-suspend-${o.id}`}>
                                                                        <Pause size={12} />
                                                                    </button>
                                                                )}
                                                                <button onClick={() => setConfirmDelete(o)} disabled={isAdmin}
                                                                    className="h-7 w-7 flex items-center justify-center text-rose-600 hover:bg-rose-50 disabled:opacity-30 rounded"
                                                                    title="Kalıcı sil" data-testid={`sa-delete-${o.id}`}>
                                                                    <Trash2 size={12} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {!owners.length && !loading && (
                                        <div className="text-center py-8 text-sm text-gray-500">Kayıt yok</div>
                                    )}
                                </div>
                            </>
                        )}

                        {tab === 'logs' && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 text-gray-600 uppercase">
                                        <tr>
                                            <th className="text-left p-2">Tarih</th>
                                            <th className="text-left p-2">Admin</th>
                                            <th className="text-left p-2">İşlem</th>
                                            <th className="text-left p-2">Hedef</th>
                                            <th className="text-left p-2">IP</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map((l) => (
                                            <tr key={l.id} className="border-b border-gray-100">
                                                <td className="p-2 text-gray-500">{(l.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                                                <td className="p-2 font-mono">{l.admin_email}</td>
                                                <td className="p-2"><span className="px-1.5 py-0.5 text-[10px] font-bold bg-gray-100 rounded">{l.action}</span></td>
                                                <td className="p-2 font-mono text-[10px] text-gray-500">{(l.target_id || '').slice(0, 8)}</td>
                                                <td className="p-2 text-gray-500">{l.ip || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {!logs.length && <div className="text-center py-8 text-sm text-gray-500">Henüz işlem yok</div>}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {confirmDelete && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-2xl">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center">
                                <AlertTriangle size={20} className="text-rose-700" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Kalıcı Silme</h3>
                        </div>
                        <p className="text-sm text-gray-700 mb-4">
                            <b>{confirmDelete.email}</b> ve şirketine ait <b>TÜM VERİLER</b> (siparişler, faturalar, stok, ayarlar, entegrasyonlar) database'den kalıcı olarak silinecek. <span className="text-rose-700 font-bold">GERİ ALINAMAZ.</span>
                        </p>
                        <p className="text-xs text-gray-600 mb-2">Devam etmek için <code className="bg-gray-100 px-1 rounded font-mono">DELETE</code> yaz:</p>
                        <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="DELETE" autoFocus
                            className="w-full h-10 px-3 text-sm border-2 border-rose-300 rounded font-mono focus:border-rose-500 outline-none"
                            data-testid="sa-confirm-input" />
                        <div className="flex items-center gap-2 mt-4">
                            <button onClick={() => { setConfirmDelete(null); setConfirmText(''); }}
                                className="flex-1 h-10 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded">Vazgeç</button>
                            <button onClick={doDelete} disabled={confirmText !== 'DELETE'}
                                className="flex-1 h-10 text-sm font-bold text-white bg-rose-700 hover:bg-rose-800 disabled:opacity-30 rounded flex items-center justify-center gap-1.5"
                                data-testid="sa-confirm-delete">
                                <Trash2 size={14} /> Kalıcı Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
}

function StatCard({ label, value, icon: Icon, color }) {
    const colorMap = {
        blue: 'bg-blue-50 text-blue-700', emerald: 'bg-emerald-50 text-emerald-700',
        amber: 'bg-amber-50 text-amber-700', purple: 'bg-purple-50 text-purple-700',
    };
    return (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</span>
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${colorMap[color]}`}><Icon size={14} /></div>
            </div>
            <div className="text-2xl font-black text-gray-900">{value}</div>
        </div>
    );
}
